import { useState, useRef } from "react";
import { toast } from "sonner";
import { PaperPlaneTilt, Paperclip, X } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PROFESSIONS = [
  "Doctor / Physician",
  "Dentist",
  "Nurse / Midwife",
  "Pharmacist",
  "Physiotherapist",
  "Allied Health Professional",
  "Clinic Owner / Manager",
  "Other",
];

const SERVICES = [
  "Medical Licensing (DHA / DOH / MOHAP)",
  "Healthcare Recruitment",
  "Clinic Consulting",
  "Interim Management Support",
  "Other",
];

const initial = { full_name: "", email: "", phone: "", profession: "", country: "", service: "", message: "" };

export default function EnquiryForm({ defaultService = "", idPrefix = "enquiry" }) {
  const [form, setForm] = useState({ ...initial, service: defaultService });
  const [cv, setCv] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setSelect = (key) => (v) => setForm((f) => ({ ...f, [key]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (cv) fd.append("cv", cv);
      await api.post("/enquiries", fd);
      toast.success("Enquiry received. We will be in touch within one working day.");
      setForm({ ...initial, service: defaultService });
      setCv(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const fieldCls =
    "bg-white border-brand-ink/15 text-brand-ink placeholder:text-brand-ink/35 focus-visible:ring-brand-green h-12";

  return (
    <form data-testid={`${idPrefix}-form`} onSubmit={onSubmit} className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-name`}>Full name *</Label>
          <Input id={`${idPrefix}-name`} data-testid={`${idPrefix}-name-input`} required minLength={2}
            value={form.full_name} onChange={set("full_name")} placeholder="Dr. Jane Smith" className={fieldCls} />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-email`}>Email *</Label>
          <Input id={`${idPrefix}-email`} data-testid={`${idPrefix}-email-input`} type="email" required
            value={form.email} onChange={set("email")} placeholder="you@example.com" className={fieldCls} />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-phone`}>Phone / WhatsApp</Label>
          <Input id={`${idPrefix}-phone`} data-testid={`${idPrefix}-phone-input`}
            value={form.phone} onChange={set("phone")} placeholder="+44 ..." className={fieldCls} />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-country`}>Current country of residence *</Label>
          <Input id={`${idPrefix}-country`} data-testid={`${idPrefix}-country-input`} required minLength={2}
            value={form.country} onChange={set("country")} placeholder="United Kingdom" className={fieldCls} />
        </div>
        <div className="space-y-2">
          <Label>Profession *</Label>
          <Select value={form.profession} onValueChange={setSelect("profession")} required>
            <SelectTrigger data-testid={`${idPrefix}-profession-select`} className={fieldCls}>
              <SelectValue placeholder="Select your profession" />
            </SelectTrigger>
            <SelectContent>
              {PROFESSIONS.map((p) => (
                <SelectItem key={p} value={p} data-testid={`${idPrefix}-profession-option-${p.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Service you are enquiring about *</Label>
          <Select value={form.service} onValueChange={setSelect("service")} required>
            <SelectTrigger data-testid={`${idPrefix}-service-select`} className={fieldCls}>
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent>
              {SERVICES.map((s) => (
                <SelectItem key={s} value={s} data-testid={`${idPrefix}-service-option-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-message`}>Tell us about your situation</Label>
        <Textarea id={`${idPrefix}-message`} data-testid={`${idPrefix}-message-input`} rows={5}
          value={form.message} onChange={set("message")}
          placeholder="e.g. I am a GP with 8 years of experience in the UK, looking to relocate to Dubai..."
          className="bg-white border-brand-ink/15 text-brand-ink placeholder:text-brand-ink/35 focus-visible:ring-brand-green" />
      </div>
      <div className="space-y-2">
        <Label className="block">Attach your CV (optional — PDF or Word, max 10MB)</Label>
        {cv ? (
          <div className="flex items-center justify-between gap-4 border border-brand-ink/15 bg-white px-4 h-12 rounded-md" data-testid={`${idPrefix}-cv-chosen`}>
            <span className="text-sm text-brand-ink truncate">{cv.name}</span>
            <button type="button" onClick={() => { setCv(null); if (fileRef.current) fileRef.current.value = ""; }}
              data-testid={`${idPrefix}-cv-remove`} className="text-brand-ink/40 hover:text-red-500 transition-colors" aria-label="Remove CV">
              <X size={16} />
            </button>
          </div>
        ) : (
          <button type="button" onClick={() => fileRef.current?.click()} data-testid={`${idPrefix}-cv-button`}
            className="inline-flex items-center gap-3 border border-dashed border-brand-green/40 bg-white px-5 h-12 rounded-md text-sm text-brand-green hover:border-brand-green hover:bg-brand-green/5 transition-colors duration-300 w-full sm:w-auto">
            <Paperclip size={16} weight="duotone" /> Choose file
          </button>
        )}
        <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" className="hidden" data-testid={`${idPrefix}-cv-input`}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (!f) return;
            if (f.size > 10 * 1024 * 1024) { toast.error("CV must be under 10MB"); e.target.value = ""; return; }
            setCv(f);
          }} />
      </div>
      <button
        type="submit"
        data-testid={`${idPrefix}-submit-button`}
        disabled={submitting}
        className="inline-flex items-center gap-3 rounded-full bg-brand-green px-8 py-4 text-sm font-semibold text-brand-bone transition-all duration-300 hover:bg-brand-pine hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
      >
        <PaperPlaneTilt size={18} weight="duotone" />
        {submitting ? "Sending..." : "Submit enquiry"}
      </button>
    </form>
  );
}
